import { Field, SelectField } from '@atlas/ui-components'
import { axisControlKind } from './axis-control'
import type { PanelAxis } from './types'

export interface AxisControlProps {
	axis: PanelAxis
	value: string
	onChange: (valueCode: string) => void
	/** The file (or batch) this control belongs to — "all files", "pack.png". */
	scope: string
	isRequired: boolean
	/** Flip once a commit has been attempted, so a fresh form does not open in red. */
	showError: boolean
	placeholder?: string
}

/**
 * One axis, as whatever control its `kind`/`value_type` calls for — the combobox
 * for a dimension or a list-valued attribute, a plain input for a `text`/`number`
 * one. Shared by the batch rail and the per-file card, which otherwise made the
 * same three-way decision twice and drifted apart on required-state handling.
 *
 * The two branches name their control differently ON PURPOSE. `SelectField` has
 * no visible-label association to lose, so `ariaLabel` is the only way to
 * distinguish a row's "Brand" from the rail's, and it is used. `Field` renders a
 * real `<label htmlFor>`: an `aria-label` there REPLACES the visible name rather
 * than extending it, leaving the user hearing "Campaign for all files" while
 * reading "Campaign". So the scoping goes on the description instead, which is
 * announced in addition to the label rather than instead of it.
 */
export function AxisControl({ axis, value, onChange, scope, isRequired, showError, placeholder }: AxisControlProps) {
	const controlKind = axisControlKind(axis)

	if (controlKind === 'select') {
		return (
			<SelectField
				label={axis.label}
				value={value}
				options={axis.options}
				onChange={onChange}
				placeholder={placeholder}
				ariaLabel={`${axis.label} for ${scope}`}
				isRequired={isRequired}
				showError={showError}
				// Every axis, not just the hierarchical ones: brand alone has 196
				// seeded values, and a picker that scrolls is a picker that reads
				// differently from the one beside it for no reason the user can see.
				isSearchable
				hasMore={axis.hasMore}
				onLoadMore={axis.onLoadMore}
				isLoadingMore={axis.isLoadingMore}
			/>
		)
	}

	return (
		<Field
			label={isRequired ? `${axis.label} *` : axis.label}
			type={controlKind}
			value={value}
			onChange={(event) => onChange(event.target.value)}
			description={`For ${scope}.${isRequired ? ' Required.' : ''}`}
			required={isRequired}
			aria-invalid={isRequired && showError && !value ? true : undefined}
		/>
	)
}
